import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    useAccountDetailQuery,
    useCreateAccountMutation,
    useSetAccountArchivedMutation,
    useUpdateAccountMutation,
} from '@/api/queries';
import type {
    AccountCreateRequest,
    AccountUpdateRequest,
} from '@/api/handlers/resources/accounts/requests';
import type {
    AccountDetail,
    AccountType,
    ChartAccount,
    LocaleCode,
    NormalBalance,
} from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import {
    accountArchiveGate,
    accountName,
    accountRoleHolder,
    collectDescendantIds,
    frozenAccountFields,
    isAccountCodeTaken,
    isStaleUpdate,
    parseGelToTetri,
    resolveAccountError,
    type AccountArchiveGate,
    type AccountErrorTarget,
    type StructuralField,
} from '../../utils';
import {
    fromAccountRoleChoice,
    fromVatRoleChoice,
    newAccountId,
    openingBalanceErrorKey,
    toAccountRoleChoice,
    toVatRoleChoice,
    type AccountRoleChoice,
    type ParentAccountOption,
    type VatRoleChoice,
} from './accountFormMeta';

type AccountFormDraft = {
    code: string;
    nameKa: string;
    nameEn: string;
    type: AccountType;
    parentId: string | null;
    normalBalance: NormalBalance;
    isContra: boolean;
    vatRole: VatRoleChoice;
    accountRole: AccountRoleChoice;
    /** Decimal string; create-only. Empty = zero opening balance. */
    openingBalance: string;
};

type UseAccountFormParams = {
    /** The row to edit; omit/`null` to create. Carries the balance, not the names. */
    account?: ChartAccount | null;
    /** The full chart — for parent options + client code-uniqueness. */
    accounts: ChartAccount[];
    locale: LocaleCode;
    /** Fired after a successful create/update so the host can close. */
    onSaved?: () => void;
};

const EMPTY_DRAFT: AccountFormDraft = {
    code: '',
    nameKa: '',
    nameEn: '',
    type: 'ASSET',
    parentId: null,
    normalBalance: 'DEBIT',
    isContra: false,
    vatRole: 'NONE',
    accountRole: 'NONE',
    openingBalance: '',
};

/** Seed the draft from the fetched detail — the only source with both names. */
const draftFromDetail = (detail: AccountDetail): AccountFormDraft => ({
    code: detail.code,
    nameKa: detail.name.ka ?? '',
    nameEn: detail.name.en ?? '',
    type: detail.type,
    parentId: detail.parentId,
    normalBalance: detail.normalBalance,
    isContra: detail.isContra,
    vatRole: toVatRoleChoice(detail.vatRole ?? null),
    accountRole: toAccountRoleChoice(detail.accountRole),
    openingBalance: '',
});

/** Which fields the user actually touched, relative to what they were shown. */
type DirtyFields = {
    code: boolean;
    name: boolean;
    parentId: boolean;
    vatRole: boolean;
    accountRole: boolean;
    type: boolean;
    normalBalance: boolean;
    isContra: boolean;
};

const diff = (
    draft: AccountFormDraft,
    baseline: AccountFormDraft,
): DirtyFields => ({
    code: draft.code.trim() !== baseline.code.trim(),
    name:
        draft.nameKa.trim() !== baseline.nameKa.trim() ||
        draft.nameEn.trim() !== baseline.nameEn.trim(),
    parentId: draft.parentId !== baseline.parentId,
    vatRole: draft.vatRole !== baseline.vatRole,
    accountRole: draft.accountRole !== baseline.accountRole,
    type: draft.type !== baseline.type,
    normalBalance: draft.normalBalance !== baseline.normalBalance,
    isContra: draft.isContra !== baseline.isContra,
});

/** The locale map to send, dropping languages the user left blank. */
const nameMapOf = (source: AccountFormDraft): AccountCreateRequest['name'] => {
    const name: Record<string, string> = {};
    if (source.nameKa.trim() !== '') {
        name.ka = source.nameKa.trim();
    }
    if (source.nameEn.trim() !== '') {
        name.en = source.nameEn.trim();
    }
    return name as AccountCreateRequest['name'];
};

/**
 * The patch body: only what changed, `null` for everything else.
 *
 * Pure, and taking both sides explicitly, because the stale-update path has to
 * build a body from freshly-rebased values that React state has not applied yet
 * — reading them out of the hook's closure there would silently use the
 * pre-rebase draft.
 *
 * Sending the whole account would clobber any field the user didn't touch with
 * whatever the form happened to be showing — which, after a concurrent edit, is
 * stale by definition.
 *
 * Two fields whose value domain includes `null` need care, because `null` is
 * also the "leave alone" sentinel:
 *
 * - **`parentId`** is only sent when it changed *and* is non-null, so "move to
 *   the root" is simply never expressed. The parent picker offers no such
 *   option, so the client never needs it (see `AccountUpdateRequest`).
 * - **`vatRole`** genuinely needs `null` as a value — it is how the user clears
 *   a VAT role — so the key is **omitted entirely** when untouched rather than
 *   set to `null`. An absent key is the standard patch "untouched" signal and
 *   every field on `AccountUpdateRequest` is optional, so this is safe; sending
 *   `null` for both meanings made clearing a VAT role a silent no-op.
 */
const buildUpdateBody = (
    source: AccountFormDraft,
    base: AccountFormDraft,
    withEtag: string,
): AccountUpdateRequest => {
    const dirty = diff(source, base);
    return {
        etag: withEtag,
        code: dirty.code ? source.code.trim() : null,
        name: dirty.name ? nameMapOf(source) : null,
        parentId:
            dirty.parentId && source.parentId !== null ? source.parentId : null,
        // Omitted, not nulled — see the note above. `accountRole` has the same
        // `null`-is-a-value problem and gets the same treatment.
        ...(dirty.vatRole
            ? { vatRole: fromVatRoleChoice(source.vatRole) }
            : {}),
        ...(dirty.accountRole
            ? { accountRole: fromAccountRoleChoice(source.accountRole) }
            : {}),
        type: dirty.type ? source.type : null,
        normalBalance: dirty.normalBalance ? source.normalBalance : null,
        isContra: dirty.isContra ? source.isContra : null,
    };
};

/** A field the other editor changed under us, described for the conflict notice. */
export type AccountFieldConflict = {
    /** i18n key (namespace `pages`) for the field's label. */
    labelKey: string;
    /** What the server now holds, rendered as text. */
    theirValue: string;
};

/**
 * All logic for the account create/edit form: the detail fetch, draft state, the
 * field-lock policy, client code-uniqueness, opening-balance parsing, the
 * patch-style save with its etag round-trip, and archive/reactivate.
 *
 * ## Why editing fetches
 *
 * A list row carries one server-resolved name; the form has separate Georgian
 * and English fields. Seeding those from the row would blank whichever language
 * the list didn't resolve, on every save. So opening the drawer reads the
 * account by id — which is also where its current `etag` comes from, so the edit
 * is made against the version the user is actually looking at.
 *
 * ## Concurrency
 *
 * The `PUT` carries the etag from that read. If someone else saved in between,
 * the server answers `409 STALE_UPDATE`. Discarding a drawer full of typing at
 * that point is how people lose work, so instead:
 *
 * 1. re-read the account;
 * 2. work out whether the other edit actually **collides** with this one — the
 *    same field, changed to a different value;
 * 3. if it doesn't, replay the user's changes onto the fresh state and resubmit
 *    with the new etag, transparently;
 * 4. if it does, show exactly which fields changed and what they now hold, and
 *    let the user press Save again to overwrite deliberately.
 *
 * The user's input survives every one of those paths.
 */
export const useAccountForm = ({
    account = null,
    accounts,
    locale,
    onSaved,
}: UseAccountFormParams) => {
    const { t } = useTranslation('pages');

    const mode: 'create' | 'edit' = account ? 'edit' : 'create';
    const editedId = account?.id ?? null;

    // The full record — both names and the etag. Create needs neither.
    const detailQuery = useAccountDetailQuery(editedId);
    const detail = detailQuery.data;

    // Client id for idempotent create; the account's own id when editing.
    const idRef = React.useRef(editedId ?? newAccountId());
    const [draft, setDraft] = React.useState<AccountFormDraft>(EMPTY_DRAFT);
    /** What the user was shown — the base for "what did they change". */
    const [baseline, setBaseline] =
        React.useState<AccountFormDraft>(EMPTY_DRAFT);
    /** The token the next write will carry. Replaced on a stale-update recovery. */
    const [etag, setEtag] = React.useState<string>('');
    const [saveError, setSaveError] = React.useState<string | null>(null);
    /** A coded failure resolved to the field it belongs on. */
    const [fieldError, setFieldError] = React.useState<{
        target: AccountErrorTarget;
        text: string;
    } | null>(null);
    const [conflicts, setConflicts] = React.useState<AccountFieldConflict[]>(
        [],
    );
    const [archiveError, setArchiveError] = React.useState<string | null>(null);
    // Reveal required-field errors only after a save attempt (a code collision
    // still surfaces live, the moment it collides — see `codeError`).
    const [submitted, setSubmitted] = React.useState(false);

    // Seed once the detail arrives (edit), or immediately (create).
    const seededFor = React.useRef<string | null>(null);
    React.useEffect(() => {
        if (mode === 'create') {
            if (seededFor.current !== 'create') {
                seededFor.current = 'create';
                idRef.current = newAccountId();
                setDraft(EMPTY_DRAFT);
                setBaseline(EMPTY_DRAFT);
                setEtag('');
            }
            return;
        }
        if (detail && seededFor.current !== detail.id) {
            seededFor.current = detail.id;
            idRef.current = detail.id;
            const seeded = draftFromDetail(detail);
            setDraft(seeded);
            setBaseline(seeded);
            setEtag(detail.etag);
            setSaveError(null);
            setFieldError(null);
            setConflicts([]);
            setArchiveError(null);
            setSubmitted(false);
        }
    }, [mode, detail]);

    // --- Field lock ---------------------------------------------------------
    // Driven by the fetched record: the list row carries `hasPostings` too, but
    // the detail is the version this edit is actually made against.
    const frozenFields: Set<StructuralField> = React.useMemo(
        () =>
            detail
                ? frozenAccountFields(detail)
                : account
                  ? frozenAccountFields(account)
                  : new Set(),
        [detail, account],
    );

    // --- Parent options: group / non-postable, non-archived, no cycles ------
    const parentOptions = React.useMemo<ParentAccountOption[]>(() => {
        const selfId = editedId;
        const excluded = selfId
            ? new Set([selfId, ...collectDescendantIds(accounts, selfId)])
            : new Set<string>();
        return accounts
            .filter(
                (candidate) =>
                    !candidate.isPostable &&
                    !candidate.archived &&
                    !excluded.has(candidate.id),
            )
            .map((candidate) => ({
                id: candidate.id,
                code: candidate.code,
                name: candidate.name,
                searchText: `${candidate.code} ${candidate.name}`,
            }))
            .sort((a, b) =>
                a.code.localeCompare(b.code, undefined, { numeric: true }),
            );
    }, [accounts, editedId]);

    // --- Validation ---------------------------------------------------------
    const trimmedCode = draft.code.trim();
    const clientCodeTaken = isAccountCodeTaken(
        trimmedCode,
        accounts,
        idRef.current,
    );
    const serverCodeTaken = fieldError?.target === 'code';
    const codeError: 'required' | 'duplicate' | null =
        trimmedCode === ''
            ? 'required'
            : clientCodeTaken || serverCodeTaken
              ? 'duplicate'
              : null;

    const nameMissing =
        draft.nameKa.trim() === '' && draft.nameEn.trim() === '';
    const parentMissing = draft.parentId === null;

    // Only one account may hold each ledger-flow role, and the server does not
    // refuse a second — it fails later, inside a flow the tagger was nowhere
    // near. Blocking the save is the only place the cause is still visible.
    const roleHolder = accountRoleHolder(
        fromAccountRoleChoice(draft.accountRole),
        accounts,
        idRef.current,
    );

    const openingParse =
        mode === 'create' && draft.openingBalance.trim() !== ''
            ? parseGelToTetri(draft.openingBalance)
            : null;
    const openingBalanceErrorKeyValue = openingParse
        ? openingBalanceErrorKey(openingParse)
        : null;

    const createMutation = useCreateAccountMutation();
    const updateMutation = useUpdateAccountMutation();
    const archiveMutation = useSetAccountArchivedMutation();
    const isSaving = createMutation.isPending || updateMutation.isPending;
    const isArchiving = archiveMutation.isPending;
    const isLoadingDetail = detailQuery.isLoading;

    const canSave =
        codeError === null &&
        !nameMissing &&
        !parentMissing &&
        roleHolder === null &&
        openingBalanceErrorKeyValue === null &&
        !isSaving &&
        !isLoadingDetail;

    // --- Archive gate -------------------------------------------------------
    const archiveGate: AccountArchiveGate | null = React.useMemo(
        () => (account ? accountArchiveGate(account, accounts) : null),
        [account, accounts],
    );

    // --- Mutators (clear stale errors on any edit) --------------------------
    const patch = React.useCallback((update: Partial<AccountFormDraft>) => {
        setSaveError(null);
        setFieldError(null);
        setDraft((prev) => ({ ...prev, ...update }));
    }, []);

    const onCodeChange = React.useCallback(
        (code: string) => patch({ code }),
        [patch],
    );
    const onNameKaChange = React.useCallback(
        (nameKa: string) => patch({ nameKa }),
        [patch],
    );
    const onNameEnChange = React.useCallback(
        (nameEn: string) => patch({ nameEn }),
        [patch],
    );
    const onTypeChange = React.useCallback(
        (type: AccountType) => patch({ type }),
        [patch],
    );
    const onParentChange = React.useCallback(
        (parentId: string | null) => patch({ parentId }),
        [patch],
    );
    const onNormalBalanceChange = React.useCallback(
        (normalBalance: NormalBalance) => patch({ normalBalance }),
        [patch],
    );
    const onIsContraChange = React.useCallback(
        (isContra: boolean) => patch({ isContra }),
        [patch],
    );
    const onVatRoleChange = React.useCallback(
        (vatRole: VatRoleChoice) => patch({ vatRole }),
        [patch],
    );
    const onAccountRoleChange = React.useCallback(
        (accountRole: AccountRoleChoice) => patch({ accountRole }),
        [patch],
    );
    const onOpeningBalanceChange = React.useCallback(
        (openingBalance: string) => patch({ openingBalance }),
        [patch],
    );

    const buildCreate = React.useCallback((): AccountCreateRequest => {
        const openingBalanceTetri = openingParse?.ok
            ? openingParse.tetri
            : undefined;
        return {
            id: idRef.current,
            code: trimmedCode,
            name: nameMapOf(draft),
            type: draft.type,
            parentId: draft.parentId,
            normalBalance: draft.normalBalance,
            isContra: draft.isContra,
            vatRole: fromVatRoleChoice(draft.vatRole),
            accountRole: fromAccountRoleChoice(draft.accountRole),
            ...(openingBalanceTetri !== undefined
                ? { openingBalanceTetri }
                : {}),
        };
    }, [draft, openingParse, trimmedCode]);

    const buildUpdate = React.useCallback(
        (withEtag: string): AccountUpdateRequest =>
            buildUpdateBody(draft, baseline, withEtag),
        [draft, baseline],
    );

    /** Turn a coded failure into the message that belongs on the right field. */
    const applyError = React.useCallback(
        (error: ErrorResponse) => {
            const resolved = resolveAccountError(error);
            const text = resolved.messageKey
                ? t(resolved.messageKey)
                : resolved.fallbackText ||
                  t('chartOfAccounts.form.errors.saveFailed');
            if (resolved.target === 'form') {
                setSaveError(text);
                setFieldError(null);
                return;
            }
            setFieldError({ target: resolved.target, text });
            setSaveError(null);
        },
        [t],
    );

    /**
     * Which of the user's edits the other editor also changed, and to what.
     * Only a genuine collision — same field, different value — is worth stopping
     * for; anything else replays cleanly.
     */
    const collisionsWith = React.useCallback(
        (fresh: AccountDetail): AccountFieldConflict[] => {
            const dirty = diff(draft, baseline);
            const theirs = draftFromDetail(fresh);
            const found: AccountFieldConflict[] = [];

            if (
                dirty.code &&
                theirs.code !== baseline.code &&
                theirs.code !== trimmedCode
            ) {
                found.push({
                    labelKey: 'chartOfAccounts.form.code',
                    theirValue: theirs.code,
                });
            }
            if (
                dirty.name &&
                (theirs.nameKa !== baseline.nameKa ||
                    theirs.nameEn !== baseline.nameEn) &&
                (theirs.nameKa !== draft.nameKa.trim() ||
                    theirs.nameEn !== draft.nameEn.trim())
            ) {
                found.push({
                    labelKey: 'chartOfAccounts.form.name',
                    theirValue: accountName(fresh, locale),
                });
            }
            if (
                dirty.parentId &&
                theirs.parentId !== baseline.parentId &&
                theirs.parentId !== draft.parentId
            ) {
                const parent = accounts.find(
                    (candidate) => candidate.id === theirs.parentId,
                );
                found.push({
                    labelKey: 'chartOfAccounts.form.parent',
                    theirValue: parent
                        ? `${parent.code} ${parent.name}`
                        : (theirs.parentId ?? '—'),
                });
            }
            return found;
        },
        [accounts, baseline, draft, locale, trimmedCode],
    );

    const onSave = React.useCallback(async () => {
        setSubmitted(true);
        if (!canSave) {
            return;
        }
        setSaveError(null);
        setFieldError(null);

        if (mode === 'create') {
            const result = await createMutation.mutateAsync(buildCreate());
            if (!result.success) {
                applyError(result.error);
                return;
            }
            onSaved?.();
            return;
        }

        const first = await updateMutation.mutateAsync({
            id: idRef.current,
            body: buildUpdate(etag),
        });
        if (first.success) {
            setConflicts([]);
            onSaved?.();
            return;
        }
        if (!isStaleUpdate(first.error)) {
            applyError(first.error);
            return;
        }

        // Someone else saved first. Re-read, and decide whether the two edits
        // actually collide before touching anything the user typed.
        const fresh = await detailQuery.refetchDetail();
        if (!fresh) {
            applyError(first.error);
            return;
        }

        const collisions = collisionsWith(fresh);
        // Replay the user's edits onto the fresh record: the baseline becomes
        // the server's state, the draft keeps only what the user actually
        // changed. Both sides move together — see `rebase`.
        const rebased = rebase(baseline, fresh, draft);
        setEtag(fresh.etag);
        setBaseline(rebased.baseline);
        setDraft(rebased.draft);

        if (collisions.length > 0) {
            // A real collision. Say exactly what changed and let the user decide
            // — pressing Save again overwrites deliberately, with the new etag.
            setConflicts(collisions);
            setSaveError(t('chartOfAccounts.form.errors.staleUpdate'));
            return;
        }

        // No collision: replay transparently. The body is built from the rebased
        // values directly rather than through `buildUpdate`, because the state
        // updates above have not been applied yet — the closure still holds the
        // pre-rebase draft.
        const replay = await updateMutation.mutateAsync({
            id: idRef.current,
            body: buildUpdateBody(rebased.draft, rebased.baseline, fresh.etag),
        });
        if (!replay.success) {
            applyError(replay.error);
            return;
        }
        setConflicts([]);
        onSaved?.();
    }, [
        applyError,
        baseline,
        buildCreate,
        buildUpdate,
        canSave,
        collisionsWith,
        createMutation,
        detailQuery,
        draft,
        etag,
        mode,
        onSaved,
        t,
        updateMutation,
    ]);

    const setArchived = React.useCallback(
        async (archived: boolean) => {
            if (!account) {
                return;
            }
            setArchiveError(null);
            const result = await archiveMutation.mutateAsync({
                id: account.id,
                archived,
            });
            if (!result.success) {
                const resolved = resolveAccountError(result.error);
                setArchiveError(
                    resolved.messageKey
                        ? t(resolved.messageKey)
                        : resolved.fallbackText ||
                              t('chartOfAccounts.form.errors.archiveFailed'),
                );
                return;
            }
            // Archive/reactivate answer 200 with a fresh etag — adopt it, or a
            // subsequent edit in this same drawer would 409 on the spent one.
            setEtag(result.response.etag);
            onSaved?.();
        },
        [account, archiveMutation, onSaved, t],
    );

    return {
        mode,
        isLoadingDetail,
        /** The by-id read failed — the form has nothing to edit. */
        detailError: detailQuery.error,
        onRetryDetail: detailQuery.refetch,
        // field values
        code: draft.code,
        nameKa: draft.nameKa,
        nameEn: draft.nameEn,
        type: draft.type,
        parentId: draft.parentId,
        normalBalance: draft.normalBalance,
        isContra: draft.isContra,
        vatRole: draft.vatRole,
        accountRole: draft.accountRole,
        openingBalance: draft.openingBalance,
        // options / lock
        parentOptions,
        frozenFields,
        // validation
        codeError,
        nameMissing,
        parentMissing,
        /**
         * The account already holding the chosen role, or `null` when the choice
         * is free. Non-null blocks the save; the View names the holder.
         */
        roleHolder: roleHolder
            ? { code: roleHolder.code, name: roleHolder.name }
            : null,
        openingBalanceErrorKey: openingBalanceErrorKeyValue,
        submitted,
        canSave,
        /** A coded failure that belongs on the parent picker, if any. */
        parentError: fieldError?.target === 'parentId' ? fieldError.text : null,
        /** A coded failure about the frozen structural fields, if any. */
        structuralError:
            fieldError?.target === 'structural' ? fieldError.text : null,
        /** Fields another editor changed under this edit. Empty when clean. */
        conflicts,
        // change handlers
        onCodeChange,
        onNameKaChange,
        onNameEnChange,
        onTypeChange,
        onParentChange,
        onNormalBalanceChange,
        onIsContraChange,
        onVatRoleChange,
        onAccountRoleChange,
        onOpeningBalanceChange,
        // save
        isSaving,
        saveError,
        onSave,
        // archive
        archived: account?.archived ?? false,
        archiveGate,
        isArchiving,
        archiveError,
        onArchive: () => setArchived(true),
        onReactivate: () => setArchived(false),
    };
};

/**
 * Replay the user's edits onto the server's new state.
 *
 * Both sides move, and that is the whole correctness argument:
 *
 * - **the baseline becomes the fresh record outright** — that is now "what I am
 *   editing from";
 * - **the draft adopts the fresh value for every field the user did not touch**,
 *   and keeps their own value for every field they did.
 *
 * The result is that `diff(draft, baseline)` is exactly the user's edits, so the
 * next save sends those and nothing else.
 *
 * Moving only the baseline — the earlier shape of this function — left the draft
 * showing pre-conflict values for untouched fields while the baseline showed the
 * new ones. Those fields then read as *dirty*, and the next save wrote the stale
 * value back, silently reverting a change the user never saw and never intended
 * to touch. That is the exact data loss the stale-update flow exists to prevent,
 * so it is covered by a test.
 *
 * The user also sees the other editor's changes appear in the untouched fields,
 * which is correct: those fields now show the current state of the account.
 */
type Rebased = {
    baseline: AccountFormDraft;
    draft: AccountFormDraft;
};

const rebase = (
    previousBaseline: AccountFormDraft,
    fresh: AccountDetail,
    currentDraft: AccountFormDraft,
): Rebased => {
    const theirs = draftFromDetail(fresh);
    const dirty = diff(currentDraft, previousBaseline);
    return {
        // Create-only, never part of the server record, and never rebased.
        baseline: {
            ...theirs,
            openingBalance: previousBaseline.openingBalance,
        },
        draft: {
            ...theirs,
            code: dirty.code ? currentDraft.code : theirs.code,
            nameKa: dirty.name ? currentDraft.nameKa : theirs.nameKa,
            nameEn: dirty.name ? currentDraft.nameEn : theirs.nameEn,
            parentId: dirty.parentId ? currentDraft.parentId : theirs.parentId,
            vatRole: dirty.vatRole ? currentDraft.vatRole : theirs.vatRole,
            accountRole: dirty.accountRole
                ? currentDraft.accountRole
                : theirs.accountRole,
            type: dirty.type ? currentDraft.type : theirs.type,
            normalBalance: dirty.normalBalance
                ? currentDraft.normalBalance
                : theirs.normalBalance,
            isContra: dirty.isContra ? currentDraft.isContra : theirs.isContra,
            openingBalance: currentDraft.openingBalance,
        },
    };
};

export type AccountFormModel = ReturnType<typeof useAccountForm>;
